import { ChannelDiff } from "src/utils/publishedDraftDiff";
import {
  ChannelCompareInput,
  ChannelCompareSide,
  ChannelCompareZone,
  ChannelCompareZoneState,
} from "./channelCompareTypes";

/** The channel's default zone; listed first and selected by default. */
export const MAIN_ZONE = "zone1";

interface LayoutZoneConfig {
  id: string;
  name?: string;
}

const getLayoutZones = (
  side: ChannelCompareSide | null | undefined,
): LayoutZoneConfig[] => side?.layoutByChannel?.config?.zones ?? [];

const getZoneState = (
  key: string,
  diff: Pick<ChannelDiff, "newZones" | "deletedZones">,
): ChannelCompareZoneState => {
  if (diff.newZones.includes(key)) {
    return "added";
  }
  if (diff.deletedZones.includes(key)) {
    return "deleted";
  }
  return "normal";
};

/**
 * The zone selector's options: the union of published and draft zones, main
 * zone first, then in layout order (draft layout, then zones only the
 * published layout has). Labels come from the draft layout, falling back to
 * the published one (a deleted zone only exists there) and then the zone key.
 *
 * `diff` must already exclude the hidden (background audio) zone.
 */
export const getChannelCompareZones = (
  channel: ChannelCompareInput,
  diff: Pick<ChannelDiff, "zones" | "newZones" | "deletedZones">,
): ChannelCompareZone[] => {
  const keys = new Set([
    ...Object.keys(diff.zones),
    ...diff.newZones,
    ...diff.deletedZones,
  ]);
  const layoutZones = [
    ...getLayoutZones(channel.draft),
    ...getLayoutZones(channel),
  ];
  const labels = new Map<string, string>();
  layoutZones.forEach(({ id, name }) => {
    if (name && !labels.has(id)) {
      labels.set(id, name);
    }
  });

  const layoutOrder = layoutZones.map(({ id }) => id);
  const ordered = Array.from(
    new Set([
      MAIN_ZONE,
      ...layoutOrder,
      ...Array.from(keys).sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true }),
      ),
    ]),
  ).filter((key) => keys.has(key));

  return ordered.map((key) => ({
    key,
    label: labels.get(key) ?? key,
    state: getZoneState(key, diff),
  }));
};

/**
 * The zone to compare: the requested one while it exists, else the main zone,
 * else the first zone (undefined only when the channel has no zones). A
 * realtime update can remove the selected zone while the modal is open.
 */
export const resolveSelectedZone = (
  zones: ChannelCompareZone[],
  selectedZone?: string,
): ChannelCompareZone | undefined =>
  zones.find((zone) => zone.key === selectedZone) ??
  zones.find((zone) => zone.key === MAIN_ZONE) ??
  zones[0];
